import _ from "lodash";

import Vue from "vue";

import { machinesApi } from "@/api/machinesApi";
import { factoryMachinesApi } from "@/api/factoryMachinesApi";
import MachineComponents from "@/stores/modules/machineComponents/MachineComponentsModule";
import MachineOverview from "@/stores/modules/MachineOverview";
import MachineStatusSpeedOverride from "@/stores/modules/machineStatus/MachineStatusSpeedOverrideModule";
import MachineStatusFeedOverride from "@/stores/modules/machineStatus/MachineStatusFeedOverrideModule";

import {
  getMachineStatusAggregated,
  calculateAvailabilityAndEfficiency,
  getCurrentMachinesShift,
  getShiftKpis,
  isLicenseEnabled
} from "@/stores/helpers/machinesHelper";
import { getGoalHourOrMinuteByMachineFamily } from "@/stores/helpers/analyticsHelper";

import MachineProduction from "@/stores/modules/machineProduction/MachineProductionModule";
import ProductionStatus from "@/stores/modules/production/ProductionStatusModule";
import { enableMachineProductionEvents } from "@/common/config";
import { dataToStatus, isValidStatusData } from "@/stores/helpers/machineStatusHelper";

const ensureStatus = status => status || { color: "black", raw: undefined, timestamp: undefined };

export default {
  state: {
    currentGroupId: undefined,
    machinesId: [],
    machines: {},
    machineLoading: false,
    machinesLoading: false,
    stats: {},
    selectedId: undefined,
    lastMachineIdUpdated: undefined
  },
  getters: {
    currentGroupId: currentState => currentState.currentGroupId,
    isLoading: currentState => currentState.machinesLoading || currentState.machineLoading,
    getMachinesRaw: currentState => currentState.machines,
    getMachineById: state => id => {
      const machine = state.machines[id];
      return machine;
    },
    getAll: (state, getters) => {
      const result = state.machinesId.map(el => getters.getMachineById(el));
      return result;
    },
    getFiltered: (state, getters, context) => {
      const searchValue = context.Search.value;
      const machines = getters.getAll;
      let filteredMachines = machines;

      if (searchValue && searchValue.length > 0) {
        const values = searchValue.map(val => JSON.parse(val.v));
        filteredMachines = values
          .map(el => machines.find(machine => machine.Id === el.Id))
          .filter(el => el);
      }

      return filteredMachines;
    },
    getLastMachineUpdated: (state, getters) => getters.getMachineById(state.lastMachineIdUpdated),
    getSelected: (state, getters) => getters.getMachineById(state.selectedId),
    getStatsById: state => id => state.stats[id],
    getAggregateDayById: state => id => {
      const stats = state.stats[id];
      return stats && stats.aggregateDay;
    },
    getEfficiencyById: state => id => {
      const stats = state.stats[id];
      return stats && stats.efficiency;
    },
    getAvailabilityById: state => id => {
      const stats = state.stats[id];
      return stats && stats.availability;
    }
  },
  actions: {
    async refreshMachines({ dispatch, getters }, { groupId }) {
      let reset = false;
      const currentGroupId = getters.currentGroupId;

      if (currentGroupId !== groupId || Object.keys(getters.getMachinesRaw).length === 0) {
        reset = true;
        await dispatch("fetchMachines", { groupId });
      }

      return reset;
    },
    async fetchMachines({ commit, dispatch, getters }, { groupId, updateStatus }) {
      if (groupId) {
        commit("SET_MACHINES_LOADING", true);

        let machines = [];

        try {
          machines = await factoryMachinesApi.getAllMachines(groupId);
          // Filter only connected
          const machinesConnected = machines.filter(m => !_.isEmpty(m.Device));

          if (machines && updateStatus) {
            try {
              const machinesStatus = await factoryMachinesApi.getMachinesStatus(machinesConnected);
              const machinesEnabled = (machinesConnected || []).filter(el => isLicenseEnabled(el));
              const machinesCurrentShift = await getCurrentMachinesShift(machinesEnabled);

              machines = machines.map(machine => {
                const machineStatus = machinesStatus.find(
                  el => el.machine && el.machine.Id === machine.Id
                );
                const status = machineStatus
                  ? machineStatus.status
                  : {
                      color: "black",
                      raw: undefined,
                      imestamp: undefined
                    };

                machine.Goal = getGoalHourOrMinuteByMachineFamily(
                  machine.MachineFamily,
                  machine.Goal
                );

                const machineCurrentShift = machinesCurrentShift.find(
                  el => el.machine && el.machine.Id === machine.Id
                );
                const currentShift = machineCurrentShift && machineCurrentShift.currentShift;

                return {
                  ...machine,
                  status,
                  currentShift
                };
              });
            } catch (e) {
              console.error("[Error] getMachinesStatus error:", e);
            }
          }
        } catch (e) {
          console.error("[Error] fetchMachines error:", e);
          machines = [];
        }

        commit("RESET", machines.length > 0);
        commit("SET_CURRENT_GROUP_ID", groupId);

        if (machines.length > 0) {
          commit("SET_MACHINES", machines);

          const machine = getters.getSelected;

          if (!machine) {
            await dispatch("clearSelected");
          }
        }

        commit("SET_MACHINES_LOADING", false);
      }
    },
    async fetchMachine({ commit }, { machineId, updateStatus }) {
      let machine = undefined;

      commit("SET_MACHINE_LOADING", true);

      if (machineId) {
        try {
          machine = await machinesApi.getMachineById(machineId);

          if (updateStatus) {
            const status = await factoryMachinesApi.getSingleStatus(machine.Id);
            machine = {
              ...machine,
              status
            };
          }

          machine.Goal = getGoalHourOrMinuteByMachineFamily(machine.MachineFamily, machine.Goal);
          machine.Kpi = await getShiftKpis(machine.MachineFamily);

          commit("SET_MACHINE", machine);
        } catch (err) {
          console.error("[Error] fetchMachine failed:", err);
        }
      }

      commit("SET_MACHINE_LOADING", false);

      return machine;
    },
    /**
      Applies the selection of a specific machine. After that action, the 'getSelected' getter reacts.Accepts an args object with specified fields
      @param {string} machineId Id of machine to select
    */
    async selectMachine({ dispatch, commit }, { machineId, self }) {
      if (machineId) {
        try {
          const machine = await dispatch("fetchMachine", { machineId, updateStatus: true });

          const enableSensorsSubscription = true;
          if (machine) {
            await dispatch("clearMachineData");
            commit("SELECT_MACHINE_ID", machineId);

            await dispatch("MachineComponents/fetchMachineComponents", {
              machine,
              enableSensorsSubscription
            });
            await dispatch("fetchMachineStatusAggregated", machine);
            await dispatch("subMachineData", machine);

            return machine;
          } else {
            // Machine not found
            const msg = self.$t("errors.error_machine_not_found");
            dispatch("Alert/Errors/setVal", { message: msg }, { root: true });
            self.$router.push("/machine");
          }
        } catch (err) {
          console.error("[Error] selectMachine failed:", err);
        }
      }
    },
    async fetchMachineStatusAggregated({ commit }, machine) {
      if (machine) {
        try {
          const aggregateDay = await getMachineStatusAggregated(machine, "day");
          const machineId = machine.Id;
          const timestamp = (machine && machine.status && machine.status.timestamp) || "";

          const { efficiency, availability } = calculateAvailabilityAndEfficiency(aggregateDay);

          commit("UPDATE_AGGREGATE_DAY", { machineId, aggregateDay });
          commit("UPDATE_EFFICIENCY", { machineId, efficiency: { v: efficiency, ts: timestamp } });
          commit("UPDATE_AVAILABILITY", {
            machineId,
            availability: { v: availability, ts: timestamp }
          });
        } catch (e) {
          console.error("[Error] Fetch machine status aggregated by machine failed.", e);
        }
      }
    },
    async clearMachineData({ dispatch, getters }) {
      const machine = getters.getSelected;

      if (machine) {
        await dispatch("MachineStatusSpeedOverride/unsubscribe", machine);
        await dispatch("MachineStatusFeedOverride/unsubscribe", machine);

        if (!enableMachineProductionEvents) {
          await dispatch("MachineProduction/unsubscribe");
          await dispatch("ProductionStatus/unsubscribe");
        }
      }
    },

    clearSelected: async ({ commit, dispatch }) => {
      await dispatch("clearMachineData");
      commit("SELECT_MACHINE_ID", undefined);
    },

    subMachineData: ({ dispatch }, machine) => {
      dispatch("MachineStatusSpeedOverride/subscribe", machine);
      dispatch("MachineStatusFeedOverride/subscribe", machine);

      if (!enableMachineProductionEvents) {
        dispatch("ProductionStatus/subscribe");
        dispatch("MachineProduction/subscribe");
      }
    },

    resetAll: ({ commit }) => {
      commit("RESET", false);
    },
    async STREAM_MachineStatusChanged({ commit, dispatch, state }, payload) {
      const { machineId, data } = payload;
      const { selectedId, machines } = state;
      const machine = machines[machineId];

      if (machine && isValidStatusData(data)) {
        const status = dataToStatus(data);

        commit("UPDATE_MACHINE_STATUS", { machineId, status });

        if (selectedId === machineId) {
          await dispatch("fetchMachineStatusAggregated", machine);
        }
      }
    }
  },
  mutations: {
    SET_CURRENT_GROUP_ID: (state, groupId) => {
      Vue.set(state, "currentGroupId", groupId);
    },
    SET_MACHINE_LOADING: (state, loading) => {
      Vue.set(state, "machineLoading", loading);
    },
    SET_MACHINES_LOADING: (state, loading) => {
      Vue.set(state, "machinesLoading", loading);
    },
    SET_MACHINES: (state, machines) => {
      const newMachines = (machines || []).reduce((acc, machine) => {
        const machineId = machine && machine.Id;
        const oldMachine = state.machines[machineId];

        acc[machineId] = {
          status: ensureStatus(),
          ...(oldMachine || {}),
          ...(machine || {})
        };

        return acc;
      }, {});

      Vue.set(state, "machines", newMachines);
      Vue.set(state, "machinesId", Object.keys(newMachines));
    },
    SET_MACHINE: (state, machine) => {
      const machineId = machine && machine.Id;

      if (machineId) {
        Vue.set(state.machines, machineId, { status: ensureStatus(), ...machine });
      }
    },
    SELECT_MACHINE_ID: (state, machineId) => {
      Vue.set(state, "selectedId", machineId);
    },
    UPDATE_MACHINE_STATUS: (state, { machineId, status }) => {
      if (machineId) {
        Vue.set(state.machines[machineId], "status", ensureStatus(status));
        Vue.set(state, "lastMachineUpdated", machineId);
      }
    },
    UPDATE_AGGREGATE_DAY: (state, { machineId, aggregateDay }) => {
      Vue.set(state.stats, machineId, { ...state.stats[machineId], aggregateDay });
    },
    UPDATE_EFFICIENCY: (state, { machineId, efficiency }) => {
      Vue.set(state.stats, machineId, { ...state.stats[machineId], efficiency });
    },
    UPDATE_AVAILABILITY: (state, { machineId, availability }) => {
      Vue.set(state.stats, machineId, { ...state.stats[machineId], availability });
    },
    RESET: (state, keepSelected = false) => {
      Vue.set(
        state,
        "machines",
        (keepSelected &&
          state.selectedId && {
            [state.selectedId]: state.machines[state.selectedId]
          }) ||
          {}
      );
      Vue.set(
        state,
        "stats",
        (keepSelected &&
          state.selectedId && {
            [state.selectedId]: state.stats[state.selectedId]
          }) ||
          {}
      );
      Vue.set(
        state,
        "lastMachineIdUpdated",
        keepSelected && state.lastMachineIdUpdated === state.selectedId
          ? state.lastMachineIdUpdated
          : undefined
      );
      Vue.set(state, "machinesId", Object.keys(state.machines));
      Vue.set(state, "currentGroupId", undefined);

      if (!keepSelected) {
        Vue.set(state, "selectedId", undefined);
      }
    }
  },
  namespaced: true,
  modules: {
    MachineComponents,
    MachineOverview,
    ProductionStatus,
    MachineProduction,
    MachineStatusSpeedOverride,
    MachineStatusFeedOverride
  }
};
